import { useState, useEffect, ReactNode } from 'react';
import Link from 'next/link';
import { Link2, Calendar, Edit, Trash2, User, Building2 } from 'lucide-react';
import { DataTable, ColumnDef, ActionMenuItem, PaginationConfig } from '@/components/ui/data-table';
import { LazyImage } from '@/components/LazyImage';
import { useConnections, ConnectionsQuery, ApiConnection } from '@/lib/hooks/useConnections';
import { RELATIONSHIP_OPTIONS, ITEM_RELATIONSHIP_OPTIONS } from '@sizzlenz/shared';

/** Lookup map for relationship value → label across all relationship option sets */
const RELATIONSHIP_LABEL_MAP = new Map<string, string>(
  [...RELATIONSHIP_OPTIONS, ...ITEM_RELATIONSHIP_OPTIONS].map((o) => [o.value, o.label])
);

/** Override the header name or visibility of built-in columns by id */
export interface ColumnOverride {
  header?: string;
  hidden?: boolean;
}

interface ConnectionTableProps {
  filters: ConnectionsQuery;
  onEditConnection?: (connection: ApiConnection) => void;
  onDeleteConnection?: (connection: ApiConnection) => void;
  /** Custom action menu items (replaces default edit/delete actions when provided) */
  actions?: ActionMenuItem<ApiConnection>[];
  className?: string;
  paginationMode?: 'prev-next' | 'load-more';
  enableSelection?: boolean;
  /** Controlled selection state. If provided, selection is managed externally. */
  selectedIds?: Set<number | string>;
  /** Called when selection changes (controlled mode). */
  onSelectionChange?: (ids: Set<number | string>) => void;
  /** Called whenever the loaded connections change (useful for bulk action parents). */
  onDataChange?: (connections: ApiConnection[]) => void;
  hideFromColumn?: boolean;
  hideToColumn?: boolean;
  /** Override column headers or hide columns by column id (from, to, role, relationships, tags, created) */
  columnOverrides?: Record<string, ColumnOverride>;
  /** Bulk action buttons rendered when items are selected */
  bulkActions?: ReactNode;
  emptyState?: {
    icon?: React.ReactNode;
    title: string;
    description?: string;
  };
}

export function ConnectionTable({
  filters,
  onEditConnection,
  onDeleteConnection,
  actions: customActions,
  className,
  paginationMode = 'prev-next',
  enableSelection,
  selectedIds: externalSelectedIds,
  onSelectionChange: externalOnSelectionChange,
  onDataChange,
  hideFromColumn = false,
  hideToColumn = false,
  columnOverrides,
  bulkActions,
  emptyState,
}: ConnectionTableProps) {
  const [internalSelectedIds, setInternalSelectedIds] = useState<Set<number | string>>(new Set());

  // Use controlled or uncontrolled selection
  const selectedIds = externalSelectedIds ?? internalSelectedIds;
  const setSelectedIds = externalOnSelectionChange ?? setInternalSelectedIds;
  const [cursors, setCursors] = useState<string[]>(['']);
  const [currentPage, setCurrentPage] = useState(0);

  // Fetch data with current cursor
  const { data, isLoading } = useConnections(
    { ...filters, nextCursor: cursors[currentPage] },
    {
      staleTime: 1000 * 10,
    }
  );

  // Get current page data
  const currentPageData = data?.pages[0];
  const connections: ApiConnection[] = currentPageData?.connections || [];

  const hasNextPage = !!currentPageData?.pagination.nextCursor;
  const hasPrevPage = currentPage > 0;

  // Notify parent when connections change
  useEffect(() => {
    onDataChange?.(connections);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connections]);

  // Reset pagination when filters change
  useEffect(() => {
    setCursors(['']);
    setCurrentPage(0);
  }, [filters.fromType, filters.fromId, filters.toType, filters.toId, filters.tags?.join(',')]);

  const handleNextPage = () => {
    if (currentPageData?.pagination.nextCursor) {
      setCursors((prev) => [
        ...prev.slice(0, currentPage + 1),
        currentPageData.pagination.nextCursor!,
      ]);
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getEntityIcon = (type: string) => {
    if (type === 'user') return <User className="h-4 w-4 text-gray-400" />;
    return <Building2 className="h-4 w-4 text-gray-400" />;
  };

  const getEntityDisplay = (connection: ApiConnection, direction: 'from' | 'to') => {
    const id = direction === 'from' ? connection.fromId : connection.toId;
    const type = direction === 'from' ? connection.fromType : connection.toType;
    const enrichedEntity =
      direction === 'from'
        ? (connection as any).fromEntity
        : (connection as any).toEntity || (connection as any).entity;
    const enrichedUser =
      direction === 'from' ? (connection as any).fromUser : (connection as any).toUser;

    // For enriched user data
    if (type === 'user' && enrichedUser) {
      return (
        <div className="flex items-center gap-3">
          {enrichedUser.image ? (
            <LazyImage
              src={enrichedUser.image}
              alt={enrichedUser.name || 'User'}
              className="h-10 w-10 rounded-full object-cover"
            />
          ) : (
            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
              <User className="h-4 w-4 text-gray-400" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <Link
              href={`/users/${id}`}
              className="font-medium text-gray-900 hover:text-primary-600 truncate block cursor-pointer"
            >
              {enrichedUser.name || enrichedUser.email || `User ${id}`}
            </Link>
            {enrichedUser.email && enrichedUser.name && (
              <div className="text-sm text-gray-500 truncate">{enrichedUser.email}</div>
            )}
          </div>
        </div>
      );
    }

    // For enriched entity data
    if (type === 'entity' && enrichedEntity) {
      return (
        <div className="flex items-center gap-3">
          {enrichedEntity.image ? (
            <LazyImage
              src={enrichedEntity.image}
              alt={enrichedEntity.name_1 || 'Entity'}
              className="h-10 w-10 rounded-lg object-cover"
            />
          ) : (
            <div className="h-10 w-10 rounded-lg bg-gray-200 flex items-center justify-center">
              <Building2 className="h-4 w-4 text-gray-400" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <Link
              href={`/entities/${id}`}
              className="font-medium text-gray-900 hover:text-primary-600 truncate block cursor-pointer"
            >
              {enrichedEntity.name_1 || `Entity ${id}`}
            </Link>
            <div className="text-sm text-gray-500 capitalize">{enrichedEntity.type}</div>
          </div>
        </div>
      );
    }

    // Fallback for non-enriched data
    return (
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-gray-200 flex items-center justify-center">
          {getEntityIcon(type)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-medium text-gray-900 capitalize">
            {type} {id}
          </div>
          <div className="text-sm text-gray-500">ID: {id}</div>
        </div>
      </div>
    );
  };

  const columns: ColumnDef<ApiConnection>[] = [
    {
      id: 'from',
      header: columnOverrides?.from?.header ?? 'From',
      cell: (connection) => getEntityDisplay(connection, 'from'),
    },
    {
      id: 'to',
      header: columnOverrides?.to?.header ?? 'To',
      cell: (connection) => getEntityDisplay(connection, 'to'),
    },
    {
      id: 'role',
      header: columnOverrides?.role?.header ?? 'Role',
      cell: (connection) => {
        const role = connection.data?.role;
        return role ? (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
            {role}
          </span>
        ) : (
          <span className="text-sm text-gray-400">N/A</span>
        );
      },
    },
    {
      id: 'relationships',
      header: columnOverrides?.relationships?.header ?? 'Relationships',
      cell: (connection) => {
        const relationships: string[] = connection.data?.relationships || [];
        return relationships.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {relationships.map((rel, idx) => (
              <span
                key={idx}
                className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
              >
                {RELATIONSHIP_LABEL_MAP.get(rel) ?? rel.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-sm text-gray-400">N/A</span>
        );
      },
    },
    {
      id: 'representing',
      header: columnOverrides?.representing?.header ?? 'Representing',
      cell: (connection) => {
        const representing: Array<{ id: number; name: string }> =
          (connection.data as any)?.representing || [];
        return representing.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {representing.map((entity) => (
              <Link
                key={entity.id}
                href={`/entities/${entity.id}`}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-100 cursor-pointer"
              >
                <Building2 className="w-3 h-3" />
                {entity.name}
              </Link>
            ))}
          </div>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        );
      },
    },
    {
      id: 'tags',
      header: columnOverrides?.tags?.header ?? 'Tags',
      cell: (connection) => {
        const allTags = [...(connection.tags || []), ...(connection.systemTags || [])];
        return allTags.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {allTags.slice(0, 3).map((tag, idx) => (
              <span
                key={idx}
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700"
              >
                {tag}
              </span>
            ))}
            {allTags.length > 3 && (
              <span className="text-xs text-gray-500">+{allTags.length - 3}</span>
            )}
          </div>
        ) : (
          <span className="text-sm text-gray-400">None</span>
        );
      },
    },
    {
      id: 'created',
      header: columnOverrides?.created?.header ?? 'Created',
      cell: (connection) => (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Calendar className="h-4 w-4 text-gray-400" />
          {formatDate(connection.createdAt)}
        </div>
      ),
    },
    {
      id: 'updated',
      header: columnOverrides?.updated?.header ?? 'Last Updated',
      cell: (connection) => (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Calendar className="h-4 w-4 text-gray-400" />
          {formatDate(connection.updatedAt)}
        </div>
      ),
    },
  ];

  // Build actions: use custom actions if provided, otherwise build from edit/delete callbacks
  let actions: ActionMenuItem<ApiConnection>[];
  if (customActions) {
    actions = customActions;
  } else {
    actions = [];
    if (onEditConnection) {
      actions.push({
        label: 'Edit Connection',
        icon: <Edit className="h-4 w-4" />,
        onClick: onEditConnection,
      });
    }
    if (onDeleteConnection) {
      actions.push({
        label: 'Delete Connection',
        icon: <Trash2 className="h-4 w-4" />,
        onClick: onDeleteConnection,
        className: 'text-red-600',
      });
    }
  }

  const paginationConfig: PaginationConfig | undefined = paginationMode
    ? {
        mode: paginationMode,
        hasNextPage,
        hasPrevPage,
        onNextPage: handleNextPage,
        onPrevPage: handlePrevPage,
      }
    : undefined;

  // Filter out columns based on hide props and columnOverrides
  let visibleColumns = columns;
  if (hideFromColumn) {
    visibleColumns = visibleColumns.filter((col) => col.id !== 'from');
  }
  if (hideToColumn) {
    visibleColumns = visibleColumns.filter((col) => col.id !== 'to');
  }
  // Hide 'updated' column by default unless explicitly overridden to be shown
  if (!columnOverrides?.updated || columnOverrides.updated.hidden !== false) {
    visibleColumns = visibleColumns.filter((col) => col.id !== 'updated');
  }
  // Hide 'representing' column by default unless explicitly overridden to be shown
  if (!columnOverrides?.representing || columnOverrides.representing.hidden !== false) {
    visibleColumns = visibleColumns.filter((col) => col.id !== 'representing');
  }
  if (columnOverrides) {
    visibleColumns = visibleColumns.filter((col) => !columnOverrides[col.id]?.hidden);
  }

  return (
    <DataTable
      data={connections}
      columns={visibleColumns}
      actions={actions}
      isLoading={isLoading}
      emptyState={
        emptyState || {
          icon: <Link2 className="w-12 h-12 text-gray-300" />,
          title: 'No connections found',
          description: 'Try adjusting your filters',
        }
      }
      getItemId={(connection) => connection.id}
      selectedIds={selectedIds}
      onSelectionChange={setSelectedIds}
      bulkActions={bulkActions}
      pagination={paginationConfig}
      className={className}
      enableSelection={enableSelection}
    />
  );
}
